import { BehaviorSubject, Subject, catchError, of, tap } from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  NomChartType,
  WidgetVizTypeChange,
} from 'src/app/core/models/widget.models';
import { NomObjectClass, NomOutputFormat } from 'src/app/core/constants';
import {
  OutputSettings,
  OutputSettingsFormat,
} from 'src/app/core/models/output-settings.models';

import { FormsService } from 'src/app/core/services/forms.service';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { OutputSettingsService } from 'src/app/core/services/output-settings.service';
import { OutputSettingsTransformationService } from 'src/app/core/services/output-settings-transformation.service';
import { Widget } from 'src/app/core/models/core.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/state/app.state';
import { selectCurrentObject } from 'src/app/core/state';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { NgpRovingFocusGroup } from 'ng-primitives/roving-focus';
import { NgpRadioGroup } from 'ng-primitives/radio';
import { SuiRadioGroupDirective, SuiRadioComponent } from '@sui/radio';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { OutputSettingsFormatEditorComponent } from '../../../../shared/components/output-settings/output-settings-format-editor/output-settings-format-editor.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-widget-settings-output-settings',
  templateUrl: './widget-settings-output-settings.component.html',
  styleUrls: ['./widget-settings-output-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SuiCheckboxComponent,
    NgpRovingFocusGroup,
    NgpRadioGroup,
    SuiRadioGroupDirective,
    SuiRadioComponent,
    FaIconComponent,
    OutputSettingsFormatEditorComponent,
    TranslatePipe,
  ],
})
export class WidgetSettingsOutputSettingsComponent implements OnInit {
  @Input() widget: Widget;
  @Input() searchString$: BehaviorSubject<string>;
  @Input() vizTypeChange$: Subject<WidgetVizTypeChange>;
  @Input() massUpdate: boolean = false;
  @Input() widgets: Widget[] = [];
  @Output() formGroupChange = new EventEmitter<FormGroup>();

  outputSettings: OutputSettings | null = null;
  selectedFormatId: NomOutputFormat = NomOutputFormat.DOCX;
  formatFormGroups: Map<NomOutputFormat, FormGroup> = new Map();
  isLoading = true;
  form: FormGroup;

  // * Radio group state for override behavior (loaded from widget data)
  overrideOutputSettings = false;

  // Format tabs data
  availableFormatTabs: Array<{
    key: NomOutputFormat;
    label: string;
    icon: IconProp;
  }> = [];

  private destroyRef = inject(DestroyRef);
  private logger = inject(LoggerService);
  private fb = inject(FormBuilder);
  private formService = inject(FormsService);
  private store = inject(Store<AppState>);
  exportFormats = [];
  hasOutputSettings = false;

  constructor(
    private outputSettingsService: OutputSettingsService,
    private outputSettingsTransformationService: OutputSettingsTransformationService,
    private cdf: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      'settings.export_formats': this.fb.array([]),
      'settings.section_break_page_break_before': [false],
    });

    this.form.valueChanges
      .pipe(
        // Only emit when the form value object actually changes
        tap((value) => {
          this.formGroupChange.emit(this.form);
          this.logger.debug('Output Settings form changed:', value);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.exportFormats = this.formService.exportFormats;
  }

  ngOnInit() {
    if (this.massUpdate) {
      this.initMassUpdateMode();
    } else {
      this.initSingleWidgetMode();
    }
  }

  private initMassUpdateMode(): void {
    this.hasOutputSettings = true;

    const exportFormats = this.widgets.reduce((formats, widget) => {
      if (
        widget.settings?.export_formats &&
        Array.isArray(widget.settings.export_formats)
      ) {
        formats.push(...widget.settings.export_formats);
      }
      return formats;
    }, []);

    const uniqueFormats = Array.from(new Set(exportFormats));
    this.setExportFormats(uniqueFormats);

    const allHavePageBreak =
      this.widgets.length > 0 &&
      this.widgets.every(
        (widget) => widget?.settings?.section_break_page_break_before === true,
      );
    this.form.patchValue({
      'settings.section_break_page_break_before': allHavePageBreak,
    });

    this.initializeOverrideState();
  }

  private initSingleWidgetMode(): void {
    if (!this.widget?.settings) {
      this.setExportFormats([]);
      this.hasOutputSettings = false;
      this.isLoading = false;
      return;
    }

    this.form.patchValue({
      'settings.section_break_page_break_before':
        this.widget.settings.section_break_page_break_before,
    });

    this.initializeOverrideState();

    const outputSetingsWidgetTypes = [
      NomChartType.FeedContent,
      NomChartType.TopFacebookContent,
      NomChartType.TopOnlineContent,
      NomChartType.TopTwitterContent,
    ];

    // init export formats from widget data
    this.setExportFormats(this.widget.settings.export_formats || []);
    this.hasOutputSettings = outputSetingsWidgetTypes.includes(
      this.widget.chart_type.id,
    );
  }

  setExportFormats(selected: number[]) {
    const formArray = this.form.get(['settings.export_formats']) as FormArray;
    formArray.clear();

    if (!Array.isArray(selected)) {
      selected = [
        NomOutputFormat.DOCX,
        NomOutputFormat.CSV,
        NomOutputFormat.HTML,
      ];
    }

    selected?.forEach((value) => {
      formArray.push(this.fb.control(value));
    });
  }

  onCheckboxChange(checked: boolean, value: number) {
    const formArray: FormArray = this.form.get([
      'settings.export_formats',
    ]) as FormArray;

    if (checked) {
      formArray.push(this.fb.control(value));
    } else {
      const index = formArray.controls.findIndex((x) => x.value === value);
      formArray.removeAt(index);
    }
  }

  get selectedFormats(): number[] {
    return this.form.value['settings.export_formats'] || [];
  }

  // * Initialize override state from widget data
  private initializeOverrideState(): void {
    if (!this.massUpdate && !this.widget) {
      this.logger.warn('Widget not provided - defaulting override to false');
      this.overrideOutputSettings = false;
      return;
    }

    if (!this.massUpdate && !this.widget?.settings) {
      this.logger.warn(
        'Widget settings not found - defaulting override to false',
      );
      this.overrideOutputSettings = false;
      return;
    }

    if (this.widget) {
      this.overrideOutputSettings =
        this.widget?.settings?.override_output_settings ?? false;

      this.logger.debug(
        'Initialized override state from widget:',
        this.overrideOutputSettings,
        this.overrideOutputSettings
          ? '(Override Output Settings)'
          : '(Use current Output Settings)',
      );
    }

    if (this.massUpdate) {
      this.logger.debug(
        'Mass update mode - override state will be determined by individual widgets',
      );

      this.overrideOutputSettings = this.widgets.every((widget) =>
        widget?.settings?.override_output_settings ? true : false,
      );
    }
    this.loadActiveOutputSettings();
  }

  private loadActiveOutputSettings() {
    if (this.massUpdate) {
      this.loadOutputSettingsFromCurrentObject();
      return;
    }
    if (this.overrideOutputSettings) {
      this.loadOutputSettings(this.widget.id, NomObjectClass.WIDGET);
    } else {
      this.loadOutputSettings(
        this.widget.object_id,
        this.widget.object_class_id,
      );
    }
  }

  private loadOutputSettingsFromCurrentObject(): void {
    this.store
      .select(selectCurrentObject)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((activeObject) => {
          if (activeObject) {
            this.loadOutputSettings(
              activeObject.id,
              activeObject.object_class_id,
            );
          } else {
            this.logger.warn(
              'No active object in workspace - cannot load output settings',
            );
            this.isLoading = false;
            this.cdf.detectChanges();
          }
        }),
      )
      .subscribe();
  }

  private loadOutputSettings(objectId: string, objectClassId: number): void {
    this.isLoading = true;

    const settings$ =
      objectClassId === NomObjectClass.WIDGET
        ? this.outputSettingsService.fetchWidgetOutputFormatsSettings(objectId)
        : this.outputSettingsService.fetchObjectOutputSettings(
            objectId,
            objectClassId,
          );

    settings$
      .pipe(
        tap((outputSettings: OutputSettings) => {
          this.logger.debug(
            'Loaded output settings for:',
            objectClassId === NomObjectClass.WIDGET ? 'widget' : 'object',
            objectId,
            outputSettings,
          );

          this.outputSettings = outputSettings;
          this.initializeFormatTabs();
          this.initializeFormatFormGroups();
          this.selectDefaultFormat();
          this.isLoading = false;
          this.cdf.detectChanges();
        }),
        catchError(() => {
          this.isLoading = false;
          this.cdf.detectChanges();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private initializeFormatFormGroups(): void {
    if (!this.outputSettings?.formats) return;

    this.formatFormGroups.clear();

    this.outputSettings.formats.forEach((format) => {
      const formGroup = this.createFormatFormGroup(format);
      this.formatFormGroups.set(format.id as NomOutputFormat, formGroup);

      // * emit Form changes for Parent Component
      formGroup.valueChanges
        .pipe(
          tap(() => this.formGroupChange.emit(formGroup)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    });
  }

  private selectDefaultFormat(): void {
    if (
      this.outputSettings?.formats &&
      this.outputSettings.formats.length > 0
    ) {
      this.selectedFormatId = this.outputSettings.formats[0]
        .id as NomOutputFormat;
    }
  }

  // * API -> Form transformation using unified service
  private createFormatFormGroup(format: any): FormGroup {
    const context =
      this.outputSettingsTransformationService.createWidgetContext();

    const formGroup =
      this.outputSettingsTransformationService.transformApiToForm(
        format,
        context,
      );

    return formGroup;
  }

  getCurrentFormGroup(): FormGroup | undefined {
    return this.formatFormGroups.get(this.selectedFormatId);
  }

  private initializeFormatTabs(): void {
    if (!this.outputSettings?.formats) return;

    const formatTabMap: Record<number, { label: string; icon: IconProp }> = {
      [NomOutputFormat.DOCX]: {
        label: 'output-settings.tab.msword',
        icon: ['fas', 'file-word'],
      },
      [NomOutputFormat.CSV]: {
        label: 'output-settings.tab.csv',
        icon: ['fas', 'file-csv'],
      },
      [NomOutputFormat.HTML]: {
        label: 'output-settings.tab.html',
        icon: ['fas', 'file-code'],
      },
    };

    this.availableFormatTabs = this.outputSettings.formats
      .filter((format) => formatTabMap[format.id])
      .map((format) => ({
        key: format.id as NomOutputFormat,
        label: formatTabMap[format.id].label,
        icon: formatTabMap[format.id].icon,
      }));

    this.logger.debug('Available format tabs:', this.availableFormatTabs);
  }

  changeFormatTab(formatKey: NomOutputFormat): void {
    this.selectedFormatId = formatKey;
    this.logger.debug('%c Changed to format:', 'color: purple', formatKey);
  }

  get hasMultipleFormats(): boolean {
    return this.availableFormatTabs.length > 1;
  }

  // * Handle radio group changes
  onOverrideSettingsChange(override: boolean): void {
    this.overrideOutputSettings = override;
    this.loadActiveOutputSettings();
  }

  // * Form -> API transformation - transforms form data to format objects
  populateOutputSettingsFromFormGroups(): void {
    if (!this.outputSettings?.formats) return;

    const context =
      this.outputSettingsTransformationService.createWidgetContext();

    this.formatFormGroups.forEach((formGroup, formatId) => {
      const format = this.outputSettings.formats.find((f) => f.id === formatId);

      if (format && formGroup) {
        this.logger.debug(
          `%c Widget transforming form to API for format ${formatId}:`,
          'color: orange',
          formGroup.value,
        );

        this.outputSettingsTransformationService.transformFormToApi(
          formGroup,
          format,
          context,
        );

        this.logger.debug(
          `%c Widget API format after transformation:`,
          'color: orange',
          format,
        );
      }
    });
  }

  // ! STRUCTURED DATA TRANSFORMATION:
  // !
  // ! This method provides the "manual processing" alternative to automatic form collection.
  // ! Instead of flat fields (property_X_value: true), it returns structured API format:
  // ! formats: [{id: 1, properties: [{key: "X", selected: true}]}]
  // !
  // ! Called by WidgetSettingsDialogComponent during save to get properly formatted data.
  // getTransformedFormats_old(): any[] {
  //   if (!this.outputSettings?.formats) return [];

  //   // Create a deep copy to avoid mutating the original
  //   const formatsCopy = JSON.parse(JSON.stringify(this.outputSettings.formats));
  //   const context =
  //     this.outputSettingsTransformationService.createWidgetContext();

  //   this.formatFormGroups.forEach((formGroup, formatId) => {
  //     const format = formatsCopy.find((f: any) => f.id === formatId);

  //     if (format && formGroup) {
  //       this.outputSettingsTransformationService.transformFormToApi(
  //         formGroup,
  //         format,
  //         context,
  //       );
  //     }
  //   });

  //   return formatsCopy;
  // }

  getTransformedFormats(): Record<
    number,
    { properties: Record<string, string>; settings: Record<string, string> }
  > {
    if (!this.outputSettings?.formats) return {};

    // Deep copy to avoid mutations
    const formatsCopy: OutputSettingsFormat[] = JSON.parse(
      JSON.stringify(this.outputSettings.formats),
    );
    const context =
      this.outputSettingsTransformationService.createWidgetContext();

    const result: Record<
      number,
      {
        properties: Record<string, any>;
        settings: Record<string, any>;
        property_values: Record<string, any[]>;
        property_option_values: Record<string, Record<string, any>>;
        number_of_posts?: number;
      }
    > = {};

    this.formatFormGroups.forEach((formGroup, formatId) => {
      const format = formatsCopy.find((f) => f.id === formatId);
      if (format && formGroup) {
        this.outputSettingsTransformationService.transformFormToApi(
          formGroup,
          format,
          context,
        );
      }
    });

    for (const format of formatsCopy) {
      const properties: Record<string, any> = {};
      const settings: Record<string, any> = {};
      const property_values: Record<string, any[]> = {};
      const property_option_values: Record<string, Record<string, any>> = {};

      // properties: ['property_uri' => 'selected']
      if (Array.isArray(format.properties)) {
        for (const prop of format.properties) {
          if (prop?.selected && prop?.key) {
            properties[prop.key] = true;
          } else {
            properties[prop.key] = false;
          }
        }
      }

      // settings are nested under categories
      if (Array.isArray(format.settings)) {
        for (const category of format.settings) {
          if (!Array.isArray(category?.settings)) continue;
          for (const setting of category.settings) {
            switch (setting.value_type) {
              case 'TOGGLE':
                if (setting?.selected && typeof setting.id === 'number') {
                  settings[String(setting.id)] = true;
                }
                break;
              case 'LIST':
              case 'INPUT_NUMBER':
                settings[String(setting.id)] = setting.value || null;
                break;
            }
          }
        }
      }

      if (
        format.property_values &&
        typeof format.property_values === 'object'
      ) {
        Object.entries(format.property_values).forEach(([key, value]) => {
          if (key && value) {
            property_values[key] = value;
          }
        });
      }

      if (
        format.property_option_values &&
        typeof format.property_option_values === 'object'
      ) {
        Object.entries(format.property_option_values).forEach(
          ([key, value]) => {
            if (key && value) {
              property_option_values[key] = value;
            }
          },
        );
      }

      result[format.id] = {
        properties,
        settings,
        property_values,
        property_option_values,
      };
      result[format.id].number_of_posts = format.number_of_posts || null;
    }

    return result;
  }
}
